"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../hooks/useAuth"
import { ref, onValue } from "../lib/firebase-database"
import { db, auth } from "../firebase"
import { Button } from "../components/ui/button"
import { Card } from "../components/ui/card"
import LogoutConfirmationPopup from "../components/LogoutConfirmationPopup"
import { OwnerLayout } from "../components/OwnerLayout"
import BookingCalendar from "../components/BookingCalendar"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { ArrowLeft } from "lucide-react"

function OwnerPage() {
  const { user } = useAuth()
  const [ownedVillas, setOwnedVillas] = useState([])
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)
  const [selectedVilla, setSelectedVilla] = useState(null)
  const [activeTab, setActiveTab] = useState("villas")
  const navigate = useNavigate()
  const villaData = ownedVillas.find((villa) => villa.id === selectedVilla?.id) || {}

  useEffect(() => {
    if (!user) {
      navigate("/login")
      return
    }

    const villasRef = ref(db, "villas")
    const unsubscribe = onValue(villasRef, (snapshot) => {
      const villasData = snapshot.val()
      if (villasData) {
        const userVillas = Object.entries(villasData)
          .filter(([_, villa]) => villa.ownerEmail === user.email)
          .map(([id, data]) => ({ id, ...data }))
        setOwnedVillas(userVillas)

        // Set the first villa as selected by default if none is selected
        if (userVillas.length > 0 && !selectedVilla) {
          setSelectedVilla(userVillas[0])
        }
      }
    })

    return () => unsubscribe()
  }, [user, navigate, selectedVilla])

  const handleLogout = () => {
    setShowLogoutConfirmation(true)
  }

  const confirmLogout = () => {
    auth.signOut().then(() => {
      navigate("/login")
    })
  }

  const handleVillaSelect = (villa) => {
    setSelectedVilla(villa)
    setActiveTab("calendar")
  }

  return (
    <OwnerLayout user={user} onLogout={handleLogout}>
      <div className="space-y-6 mb-16 lg:mb-0">
        <div className="mb-6 md:mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-2 -ml-2 h-9 px-2"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl lg:text-4xl font-bold mb-2">Hi, {villaData.owner?.name || "Owner"}</h1>
          <p className="text-lg lg:text-xl text-gray-600">Manage Your Houses In Few Clicks !</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4 w-full md:w-auto grid grid-cols-2 md:flex">
            <TabsTrigger value="villas" className="text-sm md:text-base">My Villas</TabsTrigger>
            <TabsTrigger value="calendar" disabled={!selectedVilla} className="text-sm md:text-base">
              Manage Availability
            </TabsTrigger>
          </TabsList>

          <TabsContent value="villas">
            <div className="grid grid-cols-1 gap-4 lg:gap-6">
              {ownedVillas.map((villa) => (
                <Card key={villa.id} className="overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow">
                  <div className="p-4">
                    <div className="flex flex-col md:flex-row gap-4">
                      <img
                        src={villa.thumbnail || villa.images?.[0] || "/placeholder.svg"}
                        alt={villa.name}
                        className="w-full md:w-1/3 h-48 object-cover rounded-lg"
                      />
                      <div className="flex-1 flex flex-col">
                        <div className="flex-1">
                          <h3 className="text-lg lg:text-xl font-bold mb-2">{villa.name}</h3>
                          <p className="text-gray-600 mb-4 text-sm lg:text-base">{villa.address}</p>
                        </div>
                        <div className="grid grid-cols-1 gap-2">
                          <Button 
                            className="w-full py-3 text-sm font-medium" 
                            onClick={() => handleVillaSelect(villa)}
                          >
                            Manage Availability
                          </Button>
                          <Button
                            variant="outline"
                            className="w-full py-3 text-sm font-medium"
                            onClick={() => navigate(`/edit-property/${villa.id}`)}
                          >
                            Update Property Details
                          </Button>
                          <Button 
                            variant="secondary"
                            className="w-full py-3 text-sm font-medium" 
                            onClick={() => navigate(`/villa/${villa.id}`)}
                          >
                            Go to Property Page
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="calendar">
            {selectedVilla && (
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-lg">
                <div className="flex flex-col gap-4">
                  <div className="flex gap-2 overflow-x-auto pb-2 -mx-1">
                    {ownedVillas.map((villa) => (
                      <Button
                        key={villa.id}
                        variant={selectedVilla.id === villa.id ? "default" : "outline"}
                        onClick={() => setSelectedVilla(villa)}
                        className="text-xs md:text-sm whitespace-nowrap px-2 md:px-3 py-1 md:py-2"
                      >
                        {villa.name}
                      </Button>
                    ))}
                  </div>

                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-4 md:mb-6">
                    <h2 className="text-lg md:text-xl font-bold">Manage Availability for {selectedVilla.name}</h2>
                    <Button variant="outline" onClick={() => setActiveTab("villas")} className="text-sm">
                      Back to Villas
                    </Button>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Select dates to mark as unavailable. These dates will be shown as booked to potential guests.
                    </p>
                  </div>

                  <BookingCalendar villaId={selectedVilla.id} isOwner={true} showCurrentMonthOnly={true} />
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {showLogoutConfirmation && (
        <LogoutConfirmationPopup onConfirm={confirmLogout} onCancel={() => setShowLogoutConfirmation(false)} />
      )}
    </OwnerLayout>
  )
}

export default OwnerPage
